import { api } from './api';

export type LessonMediaType = 'image' | 'video' | 'file';

export interface LessonMediaItem {
  asset_id: number;
  type: LessonMediaType;
  caption: string;
  alt_text: string;
  url: string;
  filename?: string | null;
  content_type?: string | null;
}

export interface Lesson {
  id: string;
  title: string;
  duration: string;
  summary: string;
  content_markdown: string;
  takeaways: string[];
  media: LessonMediaItem[];
  is_completed: boolean;
}

export interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
}

export interface QuizQuestionResult {
  id: string;
  question: string;
  options: string[];
  selected_index: number | null;
  correct_index: number;
  is_correct: boolean;
  explanation: string;
}

export interface QuizSubmitResponse {
  module_id: string;
  module_title: string;
  score: number;
  passed: boolean;
  passing_score: number;
  correct_count: number;
  total_questions: number;
  results: QuizQuestionResult[];
  is_all_completed: boolean;
  message: string;
}

export interface TrainingModuleSummary {
  id: string;
  title: string;
  description: string;
  duration: string;
  duration_minutes: number;
  category: string;
  iso_clause: string;
  lesson_count: number;
  question_count: number;
  is_completed: boolean;
  score: number | null;
  completed_at: string | null;
  progress_percent: number;
  lessons_completed: number;
  total_lessons: number;
}

export interface TrainingModuleDetail extends TrainingModuleSummary {
  lessons: Lesson[];
  quiz: QuizQuestion[];
}

export interface TrainingProgressOverview {
  total_modules: number;
  completed_modules: number;
  overall_progress_percent: number;
  average_score: number | null;
  certificate_eligible: boolean;
  total_lessons: number;
  completed_lessons: number;
  modules: TrainingModuleSummary[];
}

export interface LessonCompleteResponse {
  module_id: string;
  lesson_id: string;
  lessons_completed: number;
  total_lessons: number;
  progress_percent: number;
}

export interface CertificateVerification {
  valid: boolean;
  message: string;
  certificate: TrainingCertificate | null;
}

export interface TrainingCertificate {
  certificate_id: string;
  recipient_name: string;
  company_name: string | null;
  issue_date: string;
  average_score: number;
  modules_completed: number;
  total_modules: number;
  iso_standard: string;
  curriculum_version: string;
  verification_code: string;
  revoked?: boolean;
  revoked_at?: string | null;
}

export interface AcademyMediaAsset {
  id: number;
  public_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  url: string;
  alt_text: string;
  caption: string;
  created_at: string | null;
}

export interface AdminAcademyLesson {
  id?: string | null;
  title: string;
  duration: string;
  summary: string;
  content_markdown: string;
  takeaways: string[];
  media: LessonMediaItem[];
}

export interface AdminAcademyQuizQuestion {
  id?: string | null;
  question: string;
  options: string[];
  correct_index: number;
  explanation: string;
}

export interface AdminAcademyModuleSummary {
  slug: string;
  title: string;
  category: string;
  status: string;
  version: number;
  published_version: number | null;
  draft_version: number | null;
  lesson_count: number;
  question_count: number;
  updated_at: string | null;
}

export interface AdminAcademyModuleDetail extends AdminAcademyModuleSummary {
  description: string;
  duration: string;
  duration_minutes: number;
  iso_clause: string;
  sort_order: number;
  lessons: AdminAcademyLesson[];
  quiz: AdminAcademyQuizQuestion[];
}

export interface AdminAcademyModulePayload {
  title: string;
  description: string;
  duration: string;
  duration_minutes: number;
  category: string;
  iso_clause: string;
  sort_order: number;
  lessons: Array<{
    id?: string | null;
    title: string;
    duration: string;
    summary: string;
    content_markdown: string;
    takeaways: string[];
    media: Array<{
      asset_id: number;
      type: LessonMediaType;
      caption: string;
      alt_text: string;
    }>;
  }>;
  quiz: Array<{
    id?: string | null;
    question: string;
    options: string[];
    correct_index: number;
    explanation: string;
  }>;
}

export interface AdminAcademyContentOverview {
  initialized: boolean;
  published_module_count: number;
  draft_module_count: number;
  modules: AdminAcademyModuleSummary[];
  media: AcademyMediaAsset[];
}

class TrainingService {
  private baseUrl = '/v1/training';
  private contentUrl = '/v1/admin/training/content';

  async checkAccess(): Promise<boolean> {
    try {
      const response = await api.get<{ has_access: boolean }>(`${this.baseUrl}/access`);
      return response.data.has_access;
    } catch {
      return false;
    }
  }

  async getOverview(): Promise<TrainingProgressOverview> {
    const response = await api.get<TrainingProgressOverview>(`${this.baseUrl}/overview`);
    return response.data;
  }

  async getModules(): Promise<TrainingModuleSummary[]> {
    const response = await api.get<TrainingModuleSummary[]>(`${this.baseUrl}/modules`);
    return response.data;
  }

  async getModuleDetail(moduleId: string): Promise<TrainingModuleDetail> {
    const response = await api.get<TrainingModuleDetail>(`${this.baseUrl}/modules/${moduleId}`);
    return response.data;
  }

  async submitQuiz(moduleId: string, answers: Record<string, number>): Promise<QuizSubmitResponse> {
    const response = await api.post<QuizSubmitResponse>(
      `${this.baseUrl}/modules/${moduleId}/quiz/submit`,
      { answers }
    );
    return response.data;
  }

  async completeLesson(moduleId: string, lessonId: string): Promise<LessonCompleteResponse> {
    const response = await api.post<LessonCompleteResponse>(
      `${this.baseUrl}/modules/${moduleId}/lessons/${lessonId}/complete`
    );
    return response.data;
  }

  async getCertificate(): Promise<TrainingCertificate> {
    const response = await api.get<TrainingCertificate>(`${this.baseUrl}/certificate`);
    return response.data;
  }

  async verifyCertificate(code: string): Promise<CertificateVerification> {
    const response = await api.get<CertificateVerification>(
      `${this.baseUrl}/verify/${encodeURIComponent(code.trim())}`
    );
    return response.data;
  }

  async revokeCertificate(code: string): Promise<CertificateVerification> {
    const response = await api.post<CertificateVerification>(
      `/v1/admin/training/certificates/${encodeURIComponent(code.trim())}/revoke`
    );
    return response.data;
  }

  /** Turn a relative media path into a URL safe to use in <img>/<video>. */
  mediaUrl(url: string): string {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    const base = (api.defaults.baseURL || '').replace(/\/$/, '');
    return `${base}${url.startsWith('/') ? url : `/${url}`}`;
  }

  async getContentOverview(): Promise<AdminAcademyContentOverview> {
    const response = await api.get<AdminAcademyContentOverview>(
      `${this.contentUrl}`
    );
    return response.data;
  }

  async importCurriculum(): Promise<AdminAcademyContentOverview> {
    const response = await api.post<AdminAcademyContentOverview>(
      `${this.contentUrl}/import`
    );
    return response.data;
  }

  async getAdminModule(slug: string): Promise<AdminAcademyModuleDetail> {
    const response = await api.get<AdminAcademyModuleDetail>(
      `${this.contentUrl}/modules/${slug}`
    );
    return response.data;
  }

  async createAdminModule(
    payload: AdminAcademyModulePayload
  ): Promise<AdminAcademyModuleDetail> {
    const response = await api.post<AdminAcademyModuleDetail>(
      `${this.contentUrl}/modules`,
      payload
    );
    return response.data;
  }

  async updateAdminModule(
    slug: string,
    payload: AdminAcademyModulePayload
  ): Promise<AdminAcademyModuleDetail> {
    const response = await api.put<AdminAcademyModuleDetail>(
      `${this.contentUrl}/modules/${slug}`,
      payload
    );
    return response.data;
  }

  async publishAdminModule(slug: string): Promise<AdminAcademyModuleDetail> {
    const response = await api.post<AdminAcademyModuleDetail>(
      `${this.contentUrl}/modules/${slug}/publish`
    );
    return response.data;
  }

  async archiveAdminModule(slug: string): Promise<void> {
    await api.post(
      `${this.contentUrl}/modules/${slug}/archive`
    );
  }

  async uploadMedia(file: File, altText = '', caption = ''): Promise<AcademyMediaAsset> {
    const form = new FormData();
    form.append('file', file);
    form.append('alt_text', altText);
    form.append('caption', caption);
    const response = await api.post<AcademyMediaAsset>(
      `${this.contentUrl}/media`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async deleteMedia(assetId: number): Promise<void> {
    await api.delete(
      `${this.contentUrl}/media/${assetId}`
    );
  }
}

export const trainingService = new TrainingService();
